import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { Languages, Loader2 } from "lucide-react";
import { motion } from "framer-motion";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      nav("/app");
    } catch (e2) {
      setErr(apiErr(e2.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-1 relative bg-card border-r border-border overflow-hidden">
        <img
          src="https://images.pexels.com/photos/14314638/pexels-photo-14314638.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Freed Words"
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        <div className="relative z-10 mt-auto p-12">
          <h2 className="font-heading text-4xl font-light tracking-tighter max-w-md">
            Traduzioni che sembrano scritte da un <span className="text-brand-pink">madrelingua</span>.
          </h2>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          <Link to="/" className="flex items-center gap-2.5 mb-8">
            <img src="/logo.png" alt="Freed Words" className="h-8 w-auto" />
            <span className="font-heading text-lg font-semibold">Freed<span className="text-brand-pink">Words</span></span>
          </Link>
          <h1 className="font-heading text-3xl font-light tracking-tight mb-1">Bentornato</h1>
          <p className="text-muted-foreground text-sm mb-8">Accedi per continuare a tradurre.</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.com" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" data-testid="forgot-password-link"
                  className="text-xs text-brand-blue hover:underline">Password dimenticata?</Link>
              </div>
              <PasswordInput id="password" data-testid="login-password" required value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </div>
            {err && <p data-testid="login-error" className="text-sm text-destructive">{err}</p>}
            <Button type="submit" data-testid="login-submit" disabled={loading}
              className="w-full rounded-full bg-brand-blue hover:bg-brand-blue/90 text-white h-11">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accedi"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground text-center mt-6">
            Non hai un account?{" "}
            <Link to="/register" data-testid="go-register" className="text-brand-blue hover:underline">Registrati</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
